import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import axios from "axios";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import {
  AssignmentIndOutlined,
  DescriptionOutlined,
  FilePresentOutlined,
  FolderSpecialOutlined,
  InsertDriveFileOutlined,
  MedicalServicesOutlined,
  MessageOutlined,
  OpenInNewOutlined,
} from "@mui/icons-material";

import PdfViewer from "./PdfViewer";
import CommentsSection from "./CommentsSection";

import { getToken } from "auth/authenticate";

const TAB = Object.freeze({
  COMMENTS: 0,
  PRESCRIPTIONS: 1,
  TEST_ORDERS: 2,
  MEDICAL_CERTIFICATES: 3,
  REFERRALS: 4,
  OTHER_ATTACHMENTS: 5,
});

const AppointmentDetailsPage = ({
  aptId
}) => {

  const appointmentId = aptId?.aptId;

  const [data, setData] =
    useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const [
    selectedTab,
    setSelectedTab,
  ] = useState(
    TAB.COMMENTS,
  );

  /*
   * =========================================================
   * LOAD APPOINTMENT DETAILS
   * =========================================================
   */

  useEffect(() => {
    if (!appointmentId) {
      setError(
        "Appointment ID is unavailable.",
      );

      setLoading(false);

      return;
    }

    let active = true;

    const loadDetails =
      async () => {
        setLoading(true);
        setError("");

        try {
          const response =
            await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}appointment/${encodeURIComponent(
                appointmentId,
              )}`,
              {
                headers: {
                  Authorization: `Bearer ${getToken()}`,
                },
              },
            );

          if (!active) {
            return;
          }

          const payload =
            response?.data?.data;

          const appointment =
            Array.isArray(payload)
              ? payload[0]
              : payload;

          setData(
            appointment || null,
          );
        } catch (requestError) {
          console.error(
            "Error loading appointment details:",
            requestError,
          );

          if (!active) {
            return;
          }

          setError(
            requestError
              ?.response?.data
              ?.message ||
              requestError
                ?.message ||
              "Unable to load appointment details.",
          );
        } finally {
          if (active) {
            setLoading(false);
          }
        }
      };

    loadDetails();

    return () => {
      active = false;
    };
  }, [appointmentId]);

  /*
   * =========================================================
   * HELPERS
   * =========================================================
   */

  const getAttachmentNameFromUrl =
    (url) => {
      if (
        typeof url !==
        "string"
      ) {
        return "Attachment";
      }

      const withoutQuery =
        url.split("?")[0];

      const filename =
        withoutQuery.substring(
          withoutQuery.lastIndexOf(
            "/",
          ) + 1,
        );

      if (!filename) {
        return "Attachment";
      }

      try {
        return decodeURIComponent(
          filename,
        );
      } catch (decodeError) {
        return filename;
      }
    };

  const getAttachmentNameFromTitle =
    (title) => {
      if (
        typeof title !==
        "string"
      ) {
        return "";
      }

      const parts =
        title
          .trim()
          .split(/\s+/);

      if (
        parts.length > 2
      ) {
        return `${parts[0]} ${parts[1]}`;
      }

      return (
        parts[0] || ""
      );
    };

  /*
   * =========================================================
   * ATTACHMENT GROUPS
   * =========================================================
   */

  const {
    prescriptions,
    medicalCertificates,
    others,
  } = useMemo(() => {
    const result = {
      prescriptions: [],
      medicalCertificates:
        [],
      others: [],
    };

    const attachments =
      Array.isArray(
        data?.attachments,
      )
        ? data.attachments
        : [];

    attachments.forEach(
      (url) => {
        const name =
          getAttachmentNameFromUrl(
            url,
          );

        if (
          name.startsWith(
            "Prescription",
          )
        ) {
          result.prescriptions.push(
            url,
          );

          return;
        }

        if (
          name.startsWith(
            "Medical_Certificate",
          )
        ) {
          result.medicalCertificates.push(
            url,
          );

          return;
        }

        result.others.push(
          url,
        );
      },
    );

    return result;
  }, [data?.attachments]);

  const generatedDocuments =
    Array.isArray(
      data?.attachmentPdfString,
    )
      ? data.attachmentPdfString
      : [];

  const testOrders =
    useMemo(
      () =>
        generatedDocuments.filter(
          (attachment) =>
            getAttachmentNameFromTitle(
              attachment?.title,
            ) ===
            "Test Order",
        ),
      [generatedDocuments],
    );

  const referrals =
    useMemo(
      () =>
        generatedDocuments.filter(
          (attachment) =>
            getAttachmentNameFromTitle(
              attachment?.title,
            ) === "Referral",
        ),
      [generatedDocuments],
    );

  const generatedMedicalCertificates =
    useMemo(
      () =>
        generatedDocuments.filter(
          (attachment) =>
            getAttachmentNameFromTitle(
              attachment?.title,
            ) ===
            "Medical Certificate",
        ),
      [generatedDocuments],
    );

  /*
   * =========================================================
   * REUSABLE PRESENTATION
   * =========================================================
   */

  const renderSectionHeader = (
    icon,
    title,
    description,
  ) => (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      sx={{
        mb: 2,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,

          flexShrink: 0,

          display: "flex",
          alignItems: "center",
          justifyContent:
            "center",

          borderRadius: 2,

          bgcolor:
            "primary.50",

          color:
            "primary.main",
        }}
      >
        {icon}
      </Box>

      <Box>
        <Typography
          variant="subtitle1"
          fontWeight={700}
        >
          {title}
        </Typography>

        {description && (
          <Typography
            variant="body2"
            color="text.secondary"
          >
            {description}
          </Typography>
        )}
      </Box>
    </Stack>
  );

  const renderEmptyState = (
    message,
  ) => (
    <Box
      sx={{
        p: 3,

        border:
          "1px dashed",

        borderColor:
          "divider",

        borderRadius: 2,

        textAlign:
          "center",

        bgcolor:
          "background.default",
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
      >
        {message}
      </Typography>
    </Box>
  );

  const renderAttachmentList = (
    attachments,
    emptyMessage,
  ) => {
    if (
      !attachments?.length
    ) {
      return renderEmptyState(
        emptyMessage,
      );
    }

    return (
      <Stack spacing={1}>
        {attachments.map(
          (url) => (
            <Button
              key={url}
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              startIcon={
                <FilePresentOutlined />
              }
              endIcon={
                <OpenInNewOutlined />
              }
              sx={{
                justifyContent:
                  "space-between",

                textTransform:
                  "none",

                textAlign:
                  "left",
              }}
            >
              {getAttachmentNameFromUrl(
                url,
              )}
            </Button>
          ),
        )}
      </Stack>
    );
  };

  const renderGeneratedDocuments = (
    documents,
    emptyMessage,
  ) => {
    if (
      !documents?.length
    ) {
      return renderEmptyState(
        emptyMessage,
      );
    }

    return (
      <Stack spacing={3}>
        {documents.map(
          (
            attachment,
            index,
          ) => (
            <Paper
              key={
                attachment?.url ||
                `${attachment?.title}-${index}`
              }
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
              }}
            >
              <Stack spacing={1.5}>
                {attachment?.url && (
                  <PdfViewer
                    pdfUrl={
                      attachment.url
                    }
                  />
                )}

                {attachment?.url && (
                  <Button
                    component="a"
                    href={
                      attachment.url
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant="outlined"
                    startIcon={
                      <FilePresentOutlined />
                    }
                    endIcon={
                      <OpenInNewOutlined />
                    }
                    sx={{
                      alignSelf:
                        "flex-start",

                      textTransform:
                        "none",
                    }}
                  >
                    {getAttachmentNameFromUrl(
                      attachment
                        ?.filename ||
                        attachment
                          ?.url,
                    )}
                  </Button>
                )}
              </Stack>
            </Paper>
          ),
        )}
      </Stack>
    );
  };

  /*
   * =========================================================
   * LOADING / ERROR
   * =========================================================
   */

  if (loading) {
    return (
      <Box
        sx={{
          py: 4,

          display: "flex",

          justifyContent:
            "center",
        }}
      >
        <CircularProgress
          size={28}
        />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="warning">
        Appointment details are
        unavailable.
      </Alert>
    );
  }

  /*
   * =========================================================
   * UI
   * =========================================================
   */

  return (
    <Box
      sx={{
        width: "100%",
      }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography
            variant="h6"
            fontWeight={700}
          >
            Consultation Records
          </Typography>

          <Typography
            variant="body2"
            sx={{color:'gray'}}
          >
            View messages,
            prescriptions, reports,
            certificates and other
            appointment documents.
          </Typography>

         
        </Box>

        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Tabs
            value={selectedTab}
            onChange={(
              event,
              nextValue,
            ) =>
              setSelectedTab(
                nextValue,
              )
            }
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              borderBottom:
                "1px solid",
              borderColor:
                "divider",

              minHeight: 48,

              "& .MuiTab-root":
                {
                  minHeight: 48,
                  textTransform:
                    "none",
                  fontWeight: 600,
                },
            }}
          >
            <Tab
              label="Comments"
              icon={
                <MessageOutlined />
              }
              iconPosition="start"
            />

            <Tab
              label="Prescriptions"
              icon={
                <AssignmentIndOutlined />
              }
              iconPosition="start"
            />

            <Tab
              label="Test Orders"
              icon={
                <DescriptionOutlined />
              }
              iconPosition="start"
            />

            <Tab
              label="Medical Certificates"
              icon={
                <MedicalServicesOutlined />
              }
              iconPosition="start"
            />

            <Tab
              label="Referral Letters"
              icon={
                <InsertDriveFileOutlined />
              }
              iconPosition="start"
            />

            <Tab
              label="Other"
              icon={
                <FolderSpecialOutlined />
              }
              iconPosition="start"
            />
          </Tabs>

          <Box
            sx={{
              p: {
                xs: 1.5,
                sm: 2,
              },
            }}
          >
            {/* COMMENTS */}

            {selectedTab ===
              TAB.COMMENTS && (
              <>
                {renderSectionHeader(
                  <MessageOutlined />,
                  "Consultation Messages",
                  "Messages and comments recorded for this appointment.",
                )}

                <Box sx={{backgroundColor:"#d1d1d1", borderRadius:2, padding:2}}>
                  <CommentsSection
                  comments={
                    data?.AppointmentComments
                  }
                />
                </Box>
              </>
            )}

            {/* PRESCRIPTIONS */}

            {selectedTab ===
              TAB.PRESCRIPTIONS && (
              <>
                {renderSectionHeader(
                  <AssignmentIndOutlined />,
                  "Prescriptions",
                  "Prescription documents issued for this appointment.",
                )}

                <Stack spacing={2}>
                  {data
                    ?.prescriptionBase64PdfString ? (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                      }}
                    >
                      <PdfViewer
                        pdfUrl={
                          data.prescriptionBase64PdfString
                        }
                      />

                      <Button
                        component="a"
                        href={
                          data.prescriptionBase64PdfString
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        startIcon={
                          <OpenInNewOutlined />
                        }
                        sx={{
                          mt: 1,
                          textTransform:
                            "none",
                        }}
                      >
                        Open prescription
                      </Button>
                    </Paper>
                  ) : null}

                  {renderAttachmentList(
                    prescriptions,
                                      )}
                </Stack>
              </>
            )}

            {/* TEST ORDERS */}

            {selectedTab ===
              TAB.TEST_ORDERS && (
              <>
                {renderSectionHeader(
                  <DescriptionOutlined />,
                  "Test Orders",
                  "Laboratory or investigation requests linked to this appointment.",
                )}

                {renderGeneratedDocuments(
                  testOrders,
                  "No test orders available.",
                )}
              </>
            )}

            {/* MEDICAL CERTIFICATES */}

            {selectedTab ===
              TAB.MEDICAL_CERTIFICATES && (
              <>
                {renderSectionHeader(
                  <MedicalServicesOutlined />,
                  "Medical Certificates",
                  "Medical certificates issued for this appointment.",
                )}

                <Stack spacing={2}>
                  {renderGeneratedDocuments(
                    generatedMedicalCertificates,
                    "No generated medical certificates available.",
                  )}

                  {medicalCertificates.length >
                    0 && (
                    <>
                      <Divider />

                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                      >
                        Attached
                        Certificates
                      </Typography>

                      {renderAttachmentList(
                        medicalCertificates,
                        "No attached medical certificates available.",
                      )}
                    </>
                  )}
                </Stack>
              </>
            )}

            {/* REFERRALS */}

            {selectedTab ===
              TAB.REFERRALS && (
              <>
                {renderSectionHeader(
                  <InsertDriveFileOutlined />,
                  "Referral Letters",
                  "Referral letters created for this appointment.",
                )}

                {renderGeneratedDocuments(
                  referrals,
                  "No referral letters available.",
                )}
              </>
            )}

            {/* OTHER */}

            {selectedTab ===
              TAB.OTHER_ATTACHMENTS && (
              <>
                {renderSectionHeader(
                  <FolderSpecialOutlined />,
                  "Other Attachments",
                  "Other files linked to this appointment.",
                )}

                {renderAttachmentList(
                  others,
                  "No other attachments available.",
                )}
              </>
            )}
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
};

export default AppointmentDetailsPage;